import { useEffect, useMemo, useState } from "react";
import { LogOut, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { QuoteCard, STATUSES } from "./QuoteCard";

export const AdminDashboard = ({ user, onLogout }) => {
  const [quotes, setQuotes] = useState([]);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/quotes"); setQuotes(Array.isArray(data) ? data : []); }
    catch (e) { toast.error(apiError(e)); if (e?.response?.status === 401) onLogout(); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const update = async (id, status) => {
    try { const { data } = await api.patch(`/quotes/${id}`, { status }); setQuotes((p) => p.map((x) => (x.id === id ? data : x))); toast.success("Status updated"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this request?")) return;
    try { await api.delete(`/quotes/${id}`); setQuotes((p) => p.filter((x) => x.id !== id)); }
    catch (e) { toast.error(apiError(e)); }
  };
  const logout = async () => { await api.post("/auth/logout").catch(() => {}); onLogout(); };

  const counts = useMemo(() => Object.fromEntries(STATUSES.map((s) => [s, quotes.filter((x) => x.status === s).length])), [quotes]);
  const shown = quotes.filter((x) => (filter === "all" || x.status === filter) &&
    `${x.name} ${x.phone} ${x.vehicle} ${(x.services || []).join(" ")}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="min-h-screen bg-[#07080B] text-white">
      <header className="border-b border-white/10 sticky top-0 z-40 bg-[#07080B]/90 backdrop-blur-xl">
        <div className="mx-auto max-w-6xl px-5 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3"><img src="/velocity-badge.png" alt="" className="h-9" /><span className="font-display font-black uppercase text-sm">Quote desk</span></div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh" className="h-9 w-9 grid place-items-center border border-white/15 hover:border-[#1bb4f2]" aria-label="Refresh"><RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /></button>
            <button onClick={logout} data-testid="admin-logout" className="h-9 px-3 inline-flex items-center gap-2 border border-white/15 hover:border-[#1bb4f2] text-sm"><LogOut className="h-4 w-4" />Log out</button>
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-6xl px-5 py-10">
        <p className="eyebrow">Signed in as {user.email}</p>
        <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-px bg-white/10 border border-white/10" data-testid="admin-kpis">
          {[["all", quotes.length], ...STATUSES.map((s) => [s, counts[s]])].map(([s, n]) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`admin-filter-${s}`} className={`p-5 text-left transition-colors ${filter === s ? "bg-[#1bb4f2]/15" : "bg-[#07080B] hover:bg-white/5"}`}>
              <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-400">{s}</p>
              <p className="mt-2 font-cond font-bold text-4xl">{n}</p>
            </button>
          ))}
        </div>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, phone, vehicle, service…" data-testid="admin-search" className="mt-8 w-full h-12 bg-transparent border-b border-white/20 focus:border-[#1bb4f2] outline-none" />
        <div className="mt-8 space-y-3" data-testid="admin-dashboard-table">
          {!loading && shown.length === 0 && <p className="text-slate-400 py-16 text-center" data-testid="admin-empty">No quote requests yet.</p>}
          {shown.map((x) => <QuoteCard key={x.id} quote={x} onStatus={update} onDelete={remove} />)}
        </div>
      </main>
    </div>
  );
};
